/**
 * Staff Timeline PDF Template
 * Generates a complete history of a staff member across all cases
 */

import {
  createPDF,
  addHeader,
  addFootersToAllPages,
  addSectionTitle,
  addKeyValue,
  addStatusBadge,
  checkPageBreak,
  generateFilename,
  downloadPDF,
  addTableOfContents,
  drawTimelineMarker,
  anonymizeName,
} from '../pdfGenerator';
import {
  colors,
  fontSizes,
  page,
  statusColors,
  priorityColors,
  hexToRgb,
  formatDate,
  formatDateTime,
  formatTime,
  getStatusDisplay,
  getPriorityDisplay,
  documentTypes,
  eventIcons,
} from '../pdfStyles';

/**
 * Generate Staff Timeline PDF
 * @param {Object} staffData - Staff member data
 * @param {Array} timelineEvents - Events from staff_timeline_view
 * @param {Object} options - Export options
 * @returns {Promise<string>} - Filename
 */
export const generateStaffTimelinePDF = async (staffData, timelineEvents = [], options = {}) => {
  const { exportedBy = 'System', tenantName = 'Company', anonymize = false } = options;

  // Helper to conditionally anonymize names
  const getName = (name) => (anonymize ? anonymizeName(name) : name);

  const doc = createPDF('portrait');
  const headerOptions = {
    documentType: documentTypes.STAFF_TIMELINE,
    tenantName,
  };

  let y = addHeader(doc, headerOptions);

  // Document title
  doc.setFontSize(fontSizes.title);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.text));
  doc.text('STAFF TIMELINE REPORT', page.marginLeft, y + 5);
  y += 5;

  // Anonymization notice
  if (anonymize) {
    doc.setFontSize(fontSizes.small);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...hexToRgb(colors.warning));
    doc.text('(Names anonymized)', page.marginLeft, y + 5);
  }
  y += 10;

  // ===== TABLE OF CONTENTS =====
  // Build sections list for TOC
  const caseMap = {};
  timelineEvents.forEach((event) => {
    if (!caseMap[event.case_id]) {
      caseMap[event.case_id] = {
        case_id: event.case_id,
        case_title: event.case_title || 'Unknown Case',
        case_type: event.case_type || 'Unknown',
        case_status: event.case_status || 'unknown',
        case_priority: event.case_priority || 'normal',
        events: [],
      };
    }
    caseMap[event.case_id].events.push(event);
  });
  const cases = Object.values(caseMap);

  // Create TOC sections (page numbers will be approximate)
  const tocSections = [
    { title: 'Staff Information', page: 1 },
    { title: 'Cases Summary', page: 1 },
    ...cases.map((c, i) => ({ title: `Case: ${getName(c.case_title)}`, page: 2 + Math.floor(i / 2) })),
    { title: 'Overall Statistics', page: Math.max(2, Math.ceil(cases.length / 2) + 1) },
  ];

  y = addTableOfContents(doc, tocSections, y, headerOptions);

  // Start new page after TOC
  doc.addPage();
  y = addHeader(doc, headerOptions);

  // ===== STAFF INFORMATION =====
  y = addSectionTitle(doc, 'Staff Information', y);

  y = addKeyValue(doc, 'Name', getName(staffData.full_name), y);
  y = addKeyValue(doc, 'Employee ID', staffData.employee_id, y);
  y = addKeyValue(doc, 'Department', staffData.department || 'N/A', y);
  y = addKeyValue(doc, 'Position', staffData.position || 'N/A', y);
  y = addKeyValue(doc, 'Start Date', formatDate(staffData.start_date), y);

  // Status with badge
  doc.setFontSize(fontSizes.body);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.textLight));
  doc.text('Status:', page.marginLeft, y);
  addStatusBadge(doc, getStatusDisplay(staffData.employment_status || 'active'), page.marginLeft + 40, y, statusColors);
  y += 8;

  // ===== CASES SUMMARY =====
  y = addSectionTitle(doc, 'Cases Summary', y);

  // Cases already grouped above for TOC
  const activeCases = cases.filter((c) => c.case_status === 'active').length;
  const completedCases = cases.filter((c) => c.case_status === 'completed').length;

  y = addKeyValue(doc, 'Total Cases', cases.length.toString(), y);
  y = addKeyValue(doc, 'Active', activeCases.toString(), y, {
    valueColor: activeCases > 0 ? colors.primary : colors.text,
  });
  y = addKeyValue(doc, 'Completed', completedCases.toString(), y, {
    valueColor: completedCases > 0 ? colors.success : colors.text,
  });

  y += 8;

  // ===== CASE TIMELINES =====
  // Start timeline on new page
  doc.addPage();
  y = addHeader(doc, headerOptions);

  cases.forEach((caseInfo, caseIndex) => {
    y = checkPageBreak(doc, y, 40, headerOptions);

    // Case header separator
    doc.setDrawColor(...hexToRgb(colors.primary));
    doc.setLineWidth(1);
    doc.line(page.marginLeft, y, page.contentWidth + page.marginLeft, y);
    y += 5;

    // Case title (use case_title only, not UUID)
    doc.setFontSize(fontSizes.subheading);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...hexToRgb(colors.text));
    doc.text(`CASE: ${getName(caseInfo.case_title)}`, page.marginLeft, y);
    y += 5;

    // Case metadata line
    doc.setFontSize(fontSizes.small);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...hexToRgb(colors.textLight));

    const metaText = `Type: ${caseInfo.case_type}`;
    doc.text(metaText, page.marginLeft, y);

    // Status badge
    addStatusBadge(doc, getStatusDisplay(caseInfo.case_status), page.marginLeft + 50, y, statusColors);

    // Priority badge
    addStatusBadge(doc, getPriorityDisplay(caseInfo.case_priority), page.marginLeft + 80, y, priorityColors);

    y += 8;

    // Case events
    const sortedEvents = [...caseInfo.events].sort((a, b) => {
      return new Date(b.event_date) - new Date(a.event_date);
    });

    // Group by date
    const eventsByDate = {};
    sortedEvents.forEach((event) => {
      const dateKey = formatDate(event.event_date);
      if (!eventsByDate[dateKey]) {
        eventsByDate[dateKey] = [];
      }
      eventsByDate[dateKey].push(event);
    });

    Object.entries(eventsByDate).forEach(([dateStr, events]) => {
      y = checkPageBreak(doc, y, 15, headerOptions);

      // Date
      doc.setFontSize(fontSizes.body);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...hexToRgb(colors.secondary));
      doc.text(dateStr, page.marginLeft, y);
      y += 5;

      events.forEach((event, eventIndex) => {
        y = checkPageBreak(doc, y, 10, headerOptions);

        const icon = eventIcons[event.event_type] || '';
        const time = formatTime(event.event_date);
        const isLastEvent = eventIndex === events.length - 1;

        // Draw timeline marker
        const markerX = page.marginLeft + 2;
        drawTimelineMarker(doc, markerX, y - 1, event.event_type);

        // Draw connector line to next event (if not last)
        if (!isLastEvent) {
          doc.setDrawColor(...hexToRgb(colors.border));
          doc.setLineWidth(0.3);
          doc.line(markerX, y + 1, markerX, y + 8);
        }

        // Build event title with tags
        let titleText = event.event_title || 'Event';
        if (event.is_legal) titleText += ' [LEGAL]';
        if (event.is_acas) titleText += ' [ACAS]';

        // Event line - time first, then icon prefix, then title
        doc.setFontSize(fontSizes.small);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...hexToRgb(colors.textLight));
        doc.text(time, page.marginLeft + 8, y);
        
        // Icon/type indicator
        if (icon) {
          doc.setTextColor(...hexToRgb(colors.secondary));
          doc.text(icon, page.marginLeft + 23, y);
        }

        // Event title (bold)
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...hexToRgb(colors.text));
        const titleX = icon ? page.marginLeft + 38 : page.marginLeft + 23;
        doc.text(titleText, titleX, y);
        y += 4;

        // Description (if exists)
        if (event.event_description) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(fontSizes.tiny);
          doc.setTextColor(...hexToRgb(colors.textLight));

          const descLines = doc.splitTextToSize(event.event_description, page.contentWidth - 30);
          descLines.slice(0, 2).forEach((line) => {
            // Limit to 2 lines, indented past the timeline
            doc.text(line, page.marginLeft + 12, y);
            y += 3;
          });
        }

        y += 2;
      });

      y += 3;
    });

    y += 5;
  });

  // ===== OVERALL STATISTICS =====
  y = checkPageBreak(doc, y, 40, headerOptions);
  y = addSectionTitle(doc, 'Overall Statistics', y);

  // Calculate overall stats
  const stats = {
    total: timelineEvents.length,
    actions: timelineEvents.filter((e) => e.event_type === 'action_completed').length,
    documents: timelineEvents.filter((e) => e.event_type === 'document_uploaded').length,
    notes: timelineEvents.filter((e) => e.event_type === 'note_added').length,
    legal: timelineEvents.filter((e) => e.is_legal).length,
    acas: timelineEvents.filter((e) => e.is_acas).length,
  };

  y = addKeyValue(doc, 'Total Events', stats.total.toString(), y);
  y = addKeyValue(doc, 'Actions Completed', stats.actions.toString(), y);
  y = addKeyValue(doc, 'Documents Uploaded', stats.documents.toString(), y);
  y = addKeyValue(doc, 'Notes Added', stats.notes.toString(), y);
  y = addKeyValue(doc, 'Legally-Critical Steps', stats.legal.toString(), y, {
    labelWidth: 55,
    valueColor: stats.legal > 0 ? colors.danger : colors.text,
  });
  y = addKeyValue(doc, 'ACAS-Compliant Steps', stats.acas.toString(), y, {
    labelWidth: 55,
    valueColor: stats.acas > 0 ? colors.warning : colors.text,
  });

  y += 5;

  // ===== EXPORT METADATA =====
  y = checkPageBreak(doc, y, 20, headerOptions);
  doc.setDrawColor(...hexToRgb(colors.border));
  doc.setLineWidth(0.5);
  doc.line(page.marginLeft, y, page.contentWidth + page.marginLeft, y);
  y += 5;

  doc.setFontSize(fontSizes.small);
  doc.setTextColor(...hexToRgb(colors.textLight));
  doc.text(`Export Type: Staff Timeline (Full History)${anonymize ? ' - Anonymized' : ''}`, page.marginLeft, y);
  y += 4;
  doc.text(`Exported: ${formatDateTime(new Date())}`, page.marginLeft, y);
  y += 4;
  doc.text(`Exported By: ${exportedBy}`, page.marginLeft, y);

  // Add footers to all pages
  addFootersToAllPages(doc, {
    exportedBy,
    exportDate: new Date(),
  });

  // Generate filename and download
  const filename = generateFilename(staffData.employee_id, 'staff_timeline');
  downloadPDF(doc, filename);

  return filename;
};

export default generateStaffTimelinePDF;
